import { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Field, Stack, Button, tokens, Select } from '@hotjar/hotsauce';
import { ColFormWrapper, Form, Title } from '@hotjar/webapp/features/Registration/common/styles';
import { useOrganizationSizeChoices } from '@hotjar/webapp/hooks/useOrganizationSizeChoices';
import type { OrganizationSizeValue } from '@hotjar/webapp/hooks/useOrganizationSizeChoices';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { trackOrgSizeSubmitted } from '@hotjar/webapp/domains/activation/onboarding/tracking';
import { useOnboardingNavigation } from '../hooks/useOnboardingNavigation';

type UserId = Parameters<ReturnType<typeof useOnboardingNavigation>['getOrgDetails']>[0];

export interface OrganizationSizeProps {
  onSubmit: (orgName: OrganizationSizeValue) => void;
  userId: UserId;
}

const FormWrapper = styled(Form)`
  max-width: 465px;
  margin: 0 auto;
`;

const StyledTitle = styled(Title)`
  margin-bottom: ${tokens.spacer.x2};
`;

export const StyledField = styled(Field)`
  width: 100%;
`;

const NextButton = styled(Button)`
  width: fit-content;
}`;

export const OrganizationSize = ({ onSubmit, userId }: OrganizationSizeProps): JSX.Element => {
  const { t } = useTranslation();
  const { getOrgDetails } = useOnboardingNavigation();
  const [organizationSize, setOrganizationSize] = useState<OrganizationSizeValue | ''>(
    getOrgDetails(userId)?.organizationSize || '',
  );
  const organizationSizes = useOrganizationSizeChoices();
  const { click: trackClick } = useTrack();

  const handleSubmit = () => {
    if (organizationSize) {
      trackOrgSizeSubmitted({ orgSize: organizationSize });
      trackClick('Company details submitted', 'Next - Organization size', {});
      onSubmit(organizationSize);
    }
  };

  return (
    <ColFormWrapper data-testid="organization-steps-size">
      <FormWrapper onSubmit={handleSubmit} noValidate>
        <StyledTitle aria-live="assertive">
          {t('activation:onboarding.organization-steps.organization-size.title', {
            orgName: getOrgDetails(userId)?.organizationName,
          })}
        </StyledTitle>
        <Stack preset="form">
          <Select
            size="l"
            id="organizationSize"
            name="organizationSize"
            isRequired
            data-testid="organization-size"
            defaultValue={getOrgDetails(userId)?.organizationSize || ''}
            autoFocus
            onChange={(e) => setOrganizationSize(e.target.value as OrganizationSizeValue)}
          >
            <option value="" disabled>
              {t('activation:onboarding.organization-steps.select-placeholder')}
            </option>
            {organizationSizes.map(({ value, label }) => (
              <option value={value} key={value}>
                {t(label)}
              </option>
            ))}
          </Select>
          <NextButton
            size="l"
            type="submit"
            id="submit-step-size"
            data-testid="submit-step-size"
            isDisabled={!organizationSize || organizationSize.length < 1}
            loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
          >
            {t('activation:onboarding.organization-steps.submit-cta')}
          </NextButton>
        </Stack>
      </FormWrapper>
    </ColFormWrapper>
  );
};
